import React, {
  memo,
  type ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Badge,
  Box,
  ButtonSelect,
  Icon,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodAdvancedFilters,
  SodSearch,
  SodSort,
} from '../../../../../../../shared/components';
import {
  ABORT_ERROR,
  CF_NEU_STL,
  DEBOUNCE_TIME_FILTER_SEARCH,
  DEFAULT_AD_FILTER_PARAMS,
} from '../../../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../../shared/hooks';
import { type AdvancedFilterState } from '../../../../../../../shared/types';
import { type EntitlementColumnState } from '../../../../../../ViolationsEntitlement/data-layer';
import { detailsActions } from '../../../../../data-store/detailsSlice';
import { useFilterCache, useGetQueryMap, useGetSortMenu } from '../../hooks';
import { UsersColumnList } from '../UsersColumnList';

import './UsersControls.css';

/**
 * UsersControls Component
 *
 * Comprehensive control panel for managing entitlement users list interactions including search,
 * filtering, sorting, and column management with real-time updates, caching capabilities,
 * and responsive design for optimal user experience.
 *
 * @component
 * @returns {React.ReactElement} Control panel with search, filters, and management tools including:
 *   - Real-time search with debouncing
 *   - Advanced filtering with caching
 *   - Multi-field sorting options
 *   - Column visibility management
 *   - Total count display
 *
 * @example
 * <UsersControls />
 *
 * @dependencies
 * - Redux: filters, activeFilters, activeColumns, totalCount from state.aag.details.entitlementUsers.controls
 * - Hooks: useFilterCache, useGetQueryMap, useGetSortMenu
 * - Components: SodAdvancedFilters, SodSort, SodSearch, UsersColumnList
 */
function UsersControls(): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();

  const {
    aag: {
      details: {
        entitlementUsers: {
          controls: {
            filters,
            filterCache,
            activeFilters,
            activeColumns,
            sortInAction,
          },
          list: { totalRecords, isLoading: isListLoading },
        },
      },
    },
    user: { isMenuPinned },
  } = useTypedSelector((state) => state);

  // Hook for sort functionality
  const {
    fields,
    fieldName,
    actions,
    actionName,
    handleFieldClick,
    handleActionClick,
  } = useGetSortMenu(sortInAction);
  //  Hooks for filter
  const queryMap = useGetQueryMap();
  const { getFromCache, setCache } = useFilterCache(
    filterCache,
    detailsActions.setEntitlementUsersFiltersCache
  );

  const isClearBtn = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );

  const abortRef = useRef<((reason: string) => void) | null>(null);
  const lastRequestedKeyRef = useRef<string | null>(null);

  // Filter
  const [isFilterBarOpen, setIsFilterBarOpen] = useState(false);

  const handleFilterSearch = useCallback(
    async <T extends string>(
      key: T,
      searchText: T
    ): Promise<MenuOption[] | undefined | false> => {
      const cacheResponse = getFromCache(key, searchText);

      if (cacheResponse) {
        return cacheResponse as MenuOption[];
      }

      // Abort should only happen when the filter is same for search
      if (abortRef.current && lastRequestedKeyRef.current === key) {
        abortRef.current('New Search');
      }

      lastRequestedKeyRef.current = key;

      try {
        const fn = (
          queryMap as Record<
            string,
            (params: {
              search: string;
              key: string;
              [key: string]: unknown;
            }) => {
              abort: () => void;
              unwrap: () => Promise<MenuOption[]>;
            }
          >
        )[key];

        const controller = fn({
          ...DEFAULT_AD_FILTER_PARAMS,
          search: searchText,
          key,
        });

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        const options = response.map((item) => item);

        setCache(key, searchText, options);

        return options;
      } catch (error) {
        const err = error as { name: string; message: string };

        Logger.log('error', err.message);

        if (err.name === ABORT_ERROR) {
          return false;
        }

        return undefined;
      }
    },
    [queryMap, getFromCache, setCache]
  );

  const handleOptionsClick = useCallback(
    (key: string, method: string, selectedOptions: MenuOption[]) => {
      try {
        if (selectedOptions.length === 0) {
          const newActiveFilters = activeFilters.filter(
            (filter) => filter.value !== key
          );

          dispatch(
            detailsActions.setEntitlementUsersActiveFilters(newActiveFilters)
          );

          return;
        }

        const isFilterAvailable = activeFilters.find(
          (filter) => filter.value === key
        );
        const newActiveFilters = isFilterAvailable
          ? activeFilters.map((filter) => {
              if (filter.value === key) {
                return {
                  ...filter,
                  options: selectedOptions,
                };
              }

              return filter;
            })
          : [
              ...activeFilters,
              {
                value: key,
                method,
                options: selectedOptions,
              },
            ];

        dispatch(
          detailsActions.setEntitlementUsersActiveFilters(newActiveFilters)
        );
      } catch (error) {
        Logger.log('Error in handleOptionsClick:', error);
      }
    },
    [activeFilters, dispatch]
  );

  const handleFilterChange = useCallback(
    (
      selectedFilter: Pick<
        AdvancedFilterState,
        'value' | 'isChecked' | 'method'
      >
    ) => {
      try {
        const isFilterApplied = activeFilters.find(
          (filter) => filter.value === selectedFilter.value
        );

        if (selectedFilter.isChecked && isFilterApplied) {
          handleOptionsClick(selectedFilter.value, selectedFilter.method, []);
        }

        const modifiedFilters = filters.map((filter) => {
          if (selectedFilter.value === filter.value) {
            return {
              ...filter,
              isChecked: !filter.isChecked,
            };
          }

          return filter;
        });

        dispatch(detailsActions.setEntitlementUsersFilters(modifiedFilters));
      } catch (error) {
        Logger.log('Error in handleFilterChange:', error);
      }
    },
    [dispatch, filters, activeFilters, handleOptionsClick]
  );

  const handleClearBtn = useCallback(() => {
    dispatch(detailsActions.setEntitlementUsersActiveFilters([]));
  }, [dispatch]);

  const handleSearch = useCallback(
    (searchText: string) => {
      dispatch(detailsActions.setEntitlementUsersSearchKey(searchText));
    },
    [dispatch]
  );

  const handleColumnsChange = useCallback(
    (columns: EntitlementColumnState[]) => {
      dispatch(detailsActions.setEntitlementUsersActiveColumns(columns));
    },
    [dispatch]
  );

  const wrapperClasses = cx(
    'Sod-UsersControls',
    isMenuPinned && 'Sod-UsersControls--isMenuPinned'
  );

  const advancedFilterClasses = cx(
    'Sod-Users-SodAdvancedFilters',
    isFilterBarOpen
      ? 'SodAdvancedFilters--isOpen'
      : 'SodAdvancedFilters--isClose'
  );

  return (
    <Box className={wrapperClasses}>
      <Box className='Sod-Users-controls-wrapper'>
        <Box className='Sod-Users-controls'>
          <Box className='Sod-Users-controlsLeft'>
            <SodSearch
              searchPlaceholder={l('Search')}
              inlineMesssage={l('Type more than 2 characters to see results.')}
              searchDebounce={DEBOUNCE_TIME_FILTER_SEARCH}
              isDisabled={false}
              handleSearch={handleSearch}
              isSearchAlwaysExpanded
            />
            <ButtonSelect
              kind='rectangle'
              label={l('Filter')}
              onClick={() => setIsFilterBarOpen(!isFilterBarOpen)}
              isOpen={isFilterBarOpen}
              size='medium'
              isDisabled={isListLoading}
              prefixIcon={<Icon kind='Filter' />}
              isVisualCueEnabled={isClearBtn}
              BadgeComp={
                activeFilters.length ? (
                  <Badge kind='dot' color='primary' size='small' />
                ) : null
              }
            />
          </Box>
        </Box>
        {/* Filters */}
        <SodAdvancedFilters
          filters={filters}
          activeFilters={activeFilters}
          isClearBtn={isClearBtn}
          isDisabled={isListLoading}
          searchDebounce={DEBOUNCE_TIME_FILTER_SEARCH}
          handleFilterChange={handleFilterChange}
          handleFilterSearch={handleFilterSearch}
          handleOptionsClick={handleOptionsClick}
          handleClearBtn={handleClearBtn}
          className={advancedFilterClasses}
          isAddOrRemoveBtn={false}
        />
      </Box>

      <Box className='Sod-UsersControls-groupControls'>
        <Box className='Sod-Users-listSubheadingWrapper'>
          {!isListLoading && (
            <Typography kind='body3' color={CF_NEU_STL}>
              {/* eslint-disable-next-line react/jsx-no-literals */}
              {`${totalRecords ?? 0} ${l(
                '{count, plural, one {Item} other {Items}}',
                {
                  count: totalRecords ?? 0,
                }
              )}`}
            </Typography>
          )}
        </Box>
        <Box className='Sod-UsersControls-groupControlsArrangeBtns'>
          <SodSort
            fields={fields}
            fieldName={fieldName ? l(fieldName) : ''}
            actions={actions}
            actionName={actionName ? l(actionName) : ''}
            handleFieldClick={handleFieldClick}
            handleActionClick={handleActionClick}
            isDisabled={isListLoading}
          />
          <Box
            tag='span'
            className='Sod-UsersControls-groupControlsArrangeBtnsDivider'
          />
          <UsersColumnList
            columns={activeColumns}
            isDisabled={isListLoading}
            handleColumnsChange={handleColumnsChange}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default memo(UsersControls);
